import { supabase } from '../lib/supabase'
import type { WriteResult } from './projectWrites'
import { parseGuestEmails } from './calendarNotify'

/**
 * Diary entries Portal OS owns — a client meeting, a recce, a hold — written
 * here and pushed to the Portal Productions Google calendar (13 Sep 2026).
 *
 * Tom: "I want to be able to put 'tom meeting with client' into our portal OS
 * and not have to worry about Zeena missing it on the google calendar if she
 * checks it."
 *
 * The distinction that keeps the calendar sane, and which the whole feature
 * rests on:
 *
 *   • A SHOOT DAY belongs to a project. Portal OS still only fills a gap for
 *     those — it adds one where Google has nothing and otherwise leaves the
 *     hand-made entry alone (see portal-os-calendar.md).
 *   • An ENTRY, in this file, is Portal OS's own. It created the Google event,
 *     so it may update and delete it too.
 *   • An event a person made in Google is neither, and is never touched.
 *
 * The browser never talks to Google — no Google credential can live in a
 * front end. Writing here marks the row 'pending'; the calendar task does the
 * rest. So a new entry is in Portal OS instantly and in Google within a few
 * hours, and the UI says which.
 */

/**
 * 'duplicate' (22 Sep 2026) means the company calendar already had this
 * meeting, typed in by hand, so the worker created nothing.
 *
 * It is its own state rather than an 'error' because it is ORDINARY: Tom and
 * Zeena both write in the diary, and the same meeting getting entered twice is
 * a normal Tuesday, not a fault. It was previously expressed as a sentence
 * shoved into syncError while the row sat on 'pending' forever — invisible to
 * the app, and misleading, since 'pending' means "will be pushed" and this one
 * never will be.
 *
 * Nothing explanatory is stored with it. The dashboard shows these rows and
 * words them itself; Tom's instruction was that the screen says WHAT it is,
 * not why it happened.
 */
export type EntrySyncState = 'pending' | 'synced' | 'delete_pending' | 'error' | 'duplicate'

export interface CalendarEntry {
  id: string
  title: string
  startDate: string
  /** Inclusive last day. Same as startDate for a single day. */
  endDate: string
  /** "14:30", or '' for an all-day entry. */
  startTime: string
  endTime: string
  location: string
  notes: string
  createdByName: string
  googleEventId: string
  syncState: EntrySyncState
  syncError: string
}

export interface CalendarEntryDraft {
  title: string
  startDate: string
  endDate: string
  startTime: string
  endTime: string
  location: string
  notes: string
}

/**
 * Whether to tell anyone about a new entry, and who (22 Sep 2026; `notify`
 * added 24 Sep 2026).
 *
 * Kept OUT of CalendarEntryDraft deliberately. The draft is what the entry
 * IS — edit it later and every field still means the same thing. This is a
 * one-time instruction about a single moment: an entry that has already been
 * announced cannot be announced differently afterwards, so it has no place in
 * a shape that gets loaded back into the editor.
 */
export interface EntryNotify {
  /**
   * Send anything at all. Off by default.
   *
   * Every entry used to email the owners unconditionally, so that a meeting
   * could not go in without Zeena knowing. The Week Ahead being the front page
   * is what made that safe to relax: she sees the entry either way, so the
   * email now means "this one needs saying" instead of "this exists".
   */
  notify: boolean
  /** false = the owners. true = every staff member. Only read when `notify`. */
  notifyStaff: boolean
  /** Exactly as typed — split and checked at the boundary below. */
  guestEmails: string
}

export const NO_NOTIFICATION: EntryNotify = {
  notify: false,
  notifyStaff: false,
  guestEmails: '',
}

export function emptyEntryDraft(iso: string): CalendarEntryDraft {
  return { title: '', startDate: iso, endDate: '', startTime: '', endTime: '', location: '', notes: '' }
}

function fail(fn: string, error: { message: string; code?: string } | null): WriteResult {
  if (!error) return { ok: true }
  const detail = `${fn}: ${error.message}${error.code ? ` (${error.code})` : ''}`
  console.error(`[supabase] rpc refused — ${detail}`)
  return { ok: false, error: detail }
}

export async function fetchCalendarEntries(fromIso: string, toIso: string): Promise<CalendarEntry[]> {
  if (!supabase) return []
  const { data, error } = await supabase.rpc('list_calendar_entries', { p_from: fromIso, p_to: toIso })
  if (error) {
    console.warn(`[supabase] list_calendar_entries failed: ${error.message}`)
    return []
  }
  return (data as CalendarEntry[] | null) ?? []
}

/** Local calendar date as YYYY-MM-DD — never via Date parsing, which shifts. */
function isoToday(offsetDays = 0): string {
  const d = new Date()
  d.setDate(d.getDate() + offsetDays)
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

/**
 * Entries the worker deliberately did not push, because the same meeting was
 * already on the company calendar.
 *
 * Today onwards only. A duplicate whose date has passed needs nobody's
 * attention — the meeting happened, the calendar had it, and the unpushed row
 * harms nothing — so it drops off the dashboard by itself rather than sitting
 * there needing to be dismissed.
 */
export async function fetchDuplicateEntries(): Promise<CalendarEntry[]> {
  const entries = await fetchCalendarEntries(isoToday(), isoToday(180))
  return entries.filter((entry) => entry.syncState === 'duplicate')
}

/**
 * Writes the entry and, when asked for, queues the emails.
 *
 * The queueing is a database trigger rather than a second call from here, so
 * that an entry can never exist without its notification having at least been
 * attempted — a browser that closes between the two calls would otherwise
 * lose the email silently, which is the one failure nobody would ever notice.
 *
 * `p_notify` false means no job is queued at all. The database also clears the
 * audience in that case rather than trusting this to have done it: telling
 * nobody has to mean telling nobody, whatever the browser sent.
 */
export async function createCalendarEntry(
  draft: CalendarEntryDraft,
  notify: EntryNotify = NO_NOTIFICATION,
): Promise<WriteResult> {
  if (!supabase) return { ok: true }
  const { error } = await supabase.rpc('create_calendar_entry', {
    p_title: draft.title,
    p_start_date: draft.startDate,
    p_end_date: draft.endDate || null,
    p_start_time: draft.startTime || null,
    p_end_time: draft.endTime || null,
    p_location: draft.location || null,
    p_notes: draft.notes || null,
    p_notify: notify.notify,
    p_notify_staff: notify.notify && notify.notifyStaff,
    p_notify_guests: notify.notify ? parseGuestEmails(notify.guestEmails) : [],
  })
  return fail('create_calendar_entry', error)
}

/**
 * No audience argument, on purpose. Editing an entry sends nothing.
 *
 * A meeting that MOVES is a different message from a meeting that is made,
 * and re-mailing a guest every time a typo is fixed would teach everyone to
 * ignore the emails. If "the time changed" is worth sending one day, it wants
 * its own wording and its own trigger — not this one firing twice.
 */
export async function updateCalendarEntry(id: string, draft: CalendarEntryDraft): Promise<WriteResult> {
  if (!supabase) return { ok: true }
  const { error } = await supabase.rpc('update_calendar_entry', {
    p_id: id,
    p_title: draft.title,
    p_start_date: draft.startDate,
    p_end_date: draft.endDate || null,
    p_start_time: draft.startTime || null,
    p_end_time: draft.endTime || null,
    p_location: draft.location || null,
    p_notes: draft.notes || null,
  })
  return fail('update_calendar_entry', error)
}

/** Removes it here at once. The Google event goes on the next run — the row
    survives as a tombstone until then, because a deleted row would take the
    event id with it and strand the event in the calendar. */
export async function deleteCalendarEntry(id: string): Promise<WriteResult> {
  if (!supabase) return { ok: true }
  const { error } = await supabase.rpc('delete_calendar_entry', { p_id: id })
  return fail('delete_calendar_entry', error)
}

/** Every ISO date an entry covers, so a run appears on each of its days. */
export function entryDates(entry: CalendarEntry): string[] {
  const dates: string[] = []
  const cursor = new Date(`${entry.startDate}T00:00:00`)
  const last = new Date(`${entry.endDate || entry.startDate}T00:00:00`)
  if (Number.isNaN(cursor.getTime()) || Number.isNaN(last.getTime())) return [entry.startDate]
  let guard = 0
  while (cursor <= last && guard < 100) {
    const month = String(cursor.getMonth() + 1).padStart(2, '0')
    const day = String(cursor.getDate()).padStart(2, '0')
    dates.push(`${cursor.getFullYear()}-${month}-${day}`)
    cursor.setDate(cursor.getDate() + 1)
    guard += 1
  }
  return dates
}

/** "Wed 23 Sep" — split rather than Date-parsed, so no timezone shift. */
export function entryDayLabel(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  if (!y || !m || !d) return iso
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  })
}

/** "Wed 23 Sep · 13:00 · Goose & Gander" — the one-line summary of an entry. */
export function entryWhenWhere(entry: CalendarEntry): string {
  const days =
    entry.endDate && entry.endDate !== entry.startDate
      ? `${entryDayLabel(entry.startDate)} – ${entryDayLabel(entry.endDate)}`
      : entryDayLabel(entry.startDate)
  return [days, entry.startTime, entry.location].filter(Boolean).join('  ·  ')
}

/**
 * What the chip says about whether Google has caught up.
 *
 * Worth saying rather than hiding: the whole point of the feature is that
 * somebody else sees the entry in Google, so "is it there yet" is the
 * question the person who typed it actually has. Silence would leave them
 * checking Google to find out.
 */
export function syncLabel(entry: CalendarEntry): string {
  if (entry.syncState === 'synced') return 'On the company calendar'
  if (entry.syncState === 'duplicate') return 'Already on the company calendar'
  if (entry.syncState === 'error') return `Couldn’t reach the calendar — ${entry.syncError || 'will retry'}`
  if (entry.syncState === 'delete_pending') return 'Removing from the company calendar…'
  return entry.googleEventId ? 'Change not on the calendar yet' : 'Not on the calendar yet'
}
